import { useEffect } from 'preact/hooks';
import { services } from '../app';
import { altAzToEnu } from '../lib/math';
import type { SensorStatus } from '../sensors/orientation';
import { PointingGuard, type PauseReason } from '../sensors/pointing';
import { darkOut, loadJSON, pointingPaused, pointingPrompt, saveJSON, settings, showToast, viewMode } from '../state';
import { openedOnSharedView } from './share';
import { keptCorrection } from '../live/align';

// Pointing first: on a phone Ahillah follows where it is held. A finger drag borrows the view until the
// phone is aimed somewhere new; a phone lying flat stops following until it is lifted.

const guard = new PointingGuard();
let starting = false;
let draggedWhileStarting = false;

function follow() {
  const s = services.peek()!;
  guard.reset();
  pointingPaused.value = null;
  pointingPrompt.value = false;
  // Glide from the view on screen to where the phone points.
  s.engine.orientation.seed(s.engine.camToEnu);
  viewMode.value = 'ar';
}

/**
 * Follow the phone. Called from a tap (Point, the prompt, the welcome), so an iPhone may ask for motion access.
 * Explore chosen while this waits stops the sensors, which then answer 'idle', so the view stays where it is.
 */
export async function startPointing(): Promise<SensorStatus> {
  const s = services.peek();
  if (!s) return 'unavailable';
  pointingPrompt.value = false;
  // Tonight's correction from Align, here; none from another night or place (src/live/align.ts).
  s.engine.orientation.calibration = keptCorrection();
  const st = await s.engine.orientation.start();
  if (st === 'running') follow();
  return st;
}

/** What the person hears after asking to point. */
export function explainStart(st: SensorStatus) {
  const s = services.peek();
  if (st === 'idle') return; // Explore was chosen before the sensors answered
  if (st === 'running' && s) {
    showToast('Point your phone at the sky');
    setTimeout(() => {
      const acc = s.engine.orientation.headingAccuracy;
      if (acc > 20) showToast(`Compass accuracy is low (±${Math.round(acc)}°). Move the phone in a slow figure of eight, or tap Align.`, 7000);
      // Once, after dark: the warm screen that keeps eyes adapted to the dark.
      else if (darkOut.peek() && !settings.peek().nightMode && !loadJSON<{ v?: number }>('nightTip', {}).v) {
        saveJSON('nightTip', { v: 1 });
        showToast('Tip: Night vision in Sky view warms the screen, so your eyes stay used to the dark.', 6500);
      }
    }, 4000);
  } else if (st === 'denied') {
    showToast('Motion access was declined. Reload the page and allow “Motion & Orientation” to use pointing mode.', 6000);
  } else {
    showToast('This device has no compass or motion sensors. Drag the sky to explore instead.', 5000);
  }
}

/** Take over again after a pause (the phone was still being watched). */
export function resumePointing() {
  const s = services.peek();
  if (s?.engine.orientation.status === 'running') follow();
  else pointingPaused.value = null;
}

/** Step aside for a finger drag or a phone lying flat; the phone is still watched. */
export function pausePointing(reason: PauseReason) {
  const s = services.peek();
  if (!s) return;
  if (viewMode.peek() === 'ar') {
    s.engine.syncManualFromCamera();
    viewMode.value = 'manual';
  }
  guard.reset();
  pointingPaused.value = reason;
  if (reason === 'flat') {
    // Lying flat, the phone points at the floor: show the sky the way it last faced instead.
    s.engine.viewAz = guard.lastAz;
    s.engine.flyTo(altAzToEnu(30, guard.lastAz), undefined, 0.8);
  }
}

/**
 * The Explore button: drag from now on, until Point is tapped again. The sensors stop, which also cancels a start
 * still waiting on them (a pause for a drag or a flat phone keeps them running, to notice the phone move).
 */
export function stopPointing() {
  const s = services.peek();
  if (s && viewMode.peek() === 'ar') s.engine.syncManualFromCamera();
  s?.engine.orientation.stop();
  viewMode.value = 'manual';
  pointingPaused.value = null;
}

/** A finger has started dragging the sky. */
export function skyDragged() {
  pointingPrompt.value = false;
  if (starting) draggedWhileStarting = true;
  if (viewMode.peek() === 'ar') pausePointing('drag');
  else if (pointingPaused.peek() === 'flat') pointingPaused.value = 'drag';
}

/**
 * On opening: follow the phone straight away where the platform allows; an iPhone is offered one tap. A shared link
 * that set the view keeps showing that view, not wherever the phone points, until Point is tapped.
 */
export async function autoStartPointing() {
  const s = services.peek();
  if (!s || !settings.peek().startPointing || viewMode.peek() === 'ar' || pointingPaused.peek()) return;
  if (!matchMedia('(pointer: coarse)').matches || openedOnSharedView()) return;
  starting = true;
  draggedWhileStarting = false;
  const st = await s.engine.orientation.startQuietly();
  starting = false;
  if (st === 'needs-tap') {
    pointingPrompt.value = true;
    return;
  }
  if (st !== 'running' || viewMode.peek() === 'ar') return;
  // Already dragging by the time the sensors answered: keep the drag, follow once the phone moves.
  if (draggedWhileStarting) {
    pointingPaused.value = 'drag';
    return;
  }
  follow();
  if (!loadJSON<{ v?: number }>('pointHint', {}).v) {
    saveJSON('pointHint', { v: 1 });
    showToast('Ahillah follows your phone. Drag with a finger to look around.', 6000);
  }
}

/** Watch the phone ten times a second: pause when it lies flat, take over again when it moves. */
export function usePointingWatch() {
  useEffect(() => {
    const id = setInterval(() => {
      const s = services.peek();
      const o = s?.engine.orientation;
      if (!s || !o || o.status !== 'running') return;
      const f = o.latestForward();
      if (!f) return;
      const t = performance.now() / 1000;
      const paused = pointingPaused.peek();
      if (viewMode.peek() === 'ar') {
        if (guard.whilePointing(f, t) === 'flat') pausePointing('flat');
      } else if (paused && guard.whilePaused(paused, f, t, s.engine.interacting) === 'resume') follow();
    }, 100);
    return () => clearInterval(id);
  }, []);
}
